import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';

const initialSubcategory = {
  name: '',
  description: '',
  isActive: true,
  category: '',
};

const SubcategoriesAdmin = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subcategory, setSubcategory] = useState(initialSubcategory);
  const [search, setSearch] = useState('');

  // Fetch subcategories and categories
  useEffect(() => {
    fetch(getApiUrl('/api/admin/subcategories'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setSubcategories(Array.isArray(data) ? data : []));
    fetch(getApiUrl('/api/admin/categories'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setCategories(Array.isArray(data) ? data : []));
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubcategory(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle form submit (add or edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = editingSubcategory ? 'PUT' : 'POST';
    const url = editingSubcategory
      ? getApiUrl(`/api/admin/subcategories/${editingSubcategory._id}`)
      : getApiUrl('/api/admin/subcategories');
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'x-admin': 'true' },
      body: JSON.stringify(subcategory),
    });
    if (res.ok) {
      alert(editingSubcategory ? 'Subcategory updated!' : 'Subcategory added!');
      setShowModal(false);
      setEditingSubcategory(null);
      setSubcategory(initialSubcategory);
      // Refresh subcategory list
      fetch(getApiUrl('/api/admin/subcategories'), { headers: { 'x-admin': 'true' } })
        .then(res => res.json())
        .then(data => setSubcategories(Array.isArray(data) ? data : []));
    } else {
      alert('Failed to save subcategory');
    }
  };

  // Handle edit
  const handleEdit = (subcat) => {
    setEditingSubcategory(subcat);
    setSubcategory({
      name: subcat.name,
      description: subcat.description,
      isActive: subcat.isActive,
      category: subcat.category?._id || '',
    });
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    const res = await fetch(getApiUrl(`/api/admin/subcategories/${id}`), {
      method: 'DELETE',
      headers: { 'x-admin': 'true' },
    });
    if (res.ok) {
      setSubcategories(subcategories.filter(s => s._id !== id));
    } else {
      alert('Failed to delete subcategory');
    }
  };

  // Filter subcategories by search
  const filteredSubcategories = Array.isArray(subcategories)
    ? subcategories.filter(subcat =>
        subcat.name.toLowerCase().includes(search.toLowerCase()) ||
        (subcat.category?.name || '').toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Subcategory Management</h1>
        <button
          onClick={() => {
            setEditingSubcategory(null);
            setSubcategory(initialSubcategory);
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Subcategory
        </button>
      </div>
      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search subcategories by name or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
        </div>
      </div>
      {/* Subcategory Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Name</th>
              <th className="px-4 py-2 text-left font-semibold">Category</th>
              <th className="px-4 py-2 text-left font-semibold">Description</th>
              <th className="px-4 py-2 text-center font-semibold">Active</th>
              <th className="px-4 py-2 text-center font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubcategories.map((subcat) => (
              <tr key={subcat._id} className="border-t">
                <td className="px-4 py-2">{subcat.name}</td>
                <td className="px-4 py-2">{subcat.category?.name || '-'}</td>
                <td className="px-4 py-2">{subcat.description}</td>
                <td className="px-4 py-2 text-center">{subcat.isActive ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 flex justify-center items-center space-x-2">
                  <button onClick={() => handleEdit(subcat)} className="text-blue-600 hover:text-blue-800">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => handleDelete(subcat._id)} className="text-red-600 hover:text-red-800">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg relative">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
            <h2 className="text-xl font-semibold mb-4">{editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</h2>
            <form onSubmit={handleSubmit}>
              <input
                name="name"
                placeholder="Subcategory Name"
                value={subcategory.name}
                onChange={handleChange}
                required
                className="block mb-2 w-full border rounded px-3 py-2"
              />
              <select
                name="category"
                value={subcategory.category}
                onChange={handleChange}
                required
                className="block mb-2 w-full border rounded px-3 py-2"
              >
                <option value="">Select a category</option>
                {Array.isArray(categories)
                  ? categories
                      .filter(cat => cat.isActive)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))
                  : null}
              </select>
              <textarea
                name="description"
                placeholder="Description"
                value={subcategory.description}
                onChange={handleChange}
                className="block mb-2 w-full border rounded px-3 py-2"
              />
              <label className="block mb-2">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={subcategory.isActive}
                  onChange={handleChange}
                  className="mr-2"
                />
                Active
              </label>
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded mt-4 w-full">
                {editingSubcategory ? 'Update Subcategory' : 'Save Subcategory'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcategoriesAdmin; 